<template>
  <div class="grid-header">
    <slot name="table-header"></slot>
  </div>
  <a-spin :spinning="isLoading" style="height: 100vh; top: 50%">
    <div
      :class="oneColumn ? 'project-one-column-layout' : 'project-grid-layout'"
    >
      <div v-for="item in items" :key="item.id">
        <slot name="table-item" :item="item"></slot>
      </div>
    </div>
  </a-spin>
</template>

<script setup>
  /**
   * Компонента для вывода подгружаемого списка компонент.
   * Получение scroll-event через props.
   *
   * Пример использования
   *     <infinite-grid-layout
   *       :get-items="api.project.projectStats"
   *       :url-params="new URLSearchParams({ ... })"
   *       entity="projects"
   *     >
   *       <template #table-item="slotProps">
   *         <project-view-component :project="slotProps.item" />
   *       </template>
   *     </infinite-grid-layout>
   */

  import { ref, watch } from 'vue';

  const props = defineProps({
    title: {
      type: String,
      default: 'Заголовок',
    },
    /**
     * API метод для получения данных таблицы.
     */
    getItems: {
      type: Function,
      require: true,
    },
    /**
     * Имя сущности в ответе API, которая содержит список элементов таблицы
     */
    entity: {
      type: String,
      required: true,
    },
    /**
     * Параметры поиска URLSearchParams
     */
    urlParams: Object,
    /**
     * Индикатор достижения конца области прокрутки (нижняя граница области прокрутки в px.).
     * Является триггером для пагинации.
     */
    scrollBottom: Number,
    /**
     *  Отображение в одну колонку (используется в основном для таблиц).
     */
    oneColumn: {
      type: Boolean,
      default: false,
    },
    /**
     * Количество элементов на одной странице.
     */
    pageSize: {
      type: Number,
      default: 10,
    },
  });

  const emit = defineEmits(['update-total-count']);
  const isLoading = ref(true);
  const items = ref([]);

  const totalPages = ref(0);
  const lastPage = ref(0);
  const currentPage = ref(1);

  watch(
    () => props.urlParams,
    async () => {
      totalPages.value = 0;
      lastPage.value = 0;
      currentPage.value = 1;
      items.value = [];
      await getItems();
    }
  );

  watch(
    () => props.scrollBottom,
    async () => {
      if (
        (totalPages.value === 0 || currentPage.value < totalPages.value) &&
        currentPage.value === lastPage.value
      ) {
        currentPage.value++;
        await getItems();
      }
    }
  );

  async function getItems() {
    if (!props.urlParams) return;

    isLoading.value = true;
    const urlParams = props.urlParams ?? new URLSearchParams();
    urlParams.set('per-page', props.pageSize);
    urlParams.set('page', currentPage.value.toString());

    try {
      const response = await props.getItems(urlParams);
      emit('update-total-count', response[props.entity].total);
      totalPages.value = response[props.entity].pages.total;

      if (lastPage.value !== currentPage.value) {
        items.value = items.value.concat(response[props.entity].items);
        lastPage.value = currentPage.value;
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  }
</script>

<style lang="scss" scoped>
  @use '@/assets/styles/_colors' as *;

  .projects {
    margin-top: 30px;
    padding: 0 0 100px 0;
  }

  .project-grid-layout {
    margin-top: 30px;
    display: grid;
    gap: 20px;
    //grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
    grid-template-columns: repeat(2, 1fr);
    padding-bottom: 200px;
  }

  .project-one-column-layout {
    margin-top: 30px;
    display: grid;
    gap: 20px;
    grid-template-columns: 1fr;
    padding-bottom: 200px;
  }
</style>
